import { LoadingButton } from "@mui/lab";
import { Box, Checkbox, Dialog, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { acceptTermsHandle } from "src/api/megs-api-api";
import { useSelector } from "react-redux";
import { selectCurrentRole, selectIsAuthenticated, selectUsername } from "src/redux/authSlice";

function TermsAndConditionsModal({ open, handleClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [fullName, setFullName] = useState("");
  const [termsChecked, setTermsChecked] = useState(false);

  // Use Redux selectors (single source of truth)
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const selectedRole = useSelector(selectCurrentRole);
  const username = useSelector(selectUsername);
  const brand = selectedRole?.brand || "MegsApp";

  if (!isAuthenticated || !selectedRole) {
    return null; // Do not render the modal if the user is not authenticated
  }

  const modalContent = [
    {
      heading: "1. Introduction",
      body: [
        {
          number: "1.1",
          text: `${brand} authorizes you to act as an independent RICA agent to sell and register SIM cards in compliance with the Regulation of Interception of Communications and Provision of Communication-Related Information Act (RICA).`,
        },
        {
          number: "1.2",
          text: `You are not an employee of ${brand} but an independent contractor operating at your own risk and expense.`,
        },
      ],
    },
    {
      heading: "2. Authorization and Scope",
      body: [
        {
          number: "2.1",
          text: "You are authorized to:",
          subpoints: [
            {
              letter: "a",
              text: `Sell and register ${brand} SIM cards.`,
            },
            {
              letter: "b",
              text: "Ensure all customer registrations comply fully with RICA regulations.",
            },
            {
              letter: "c",
              text: `Promote ${brand} products and services as per the training and guidelines provided.`,
            },
          ],
        },
        {
          number: "2.2",
          text: "You are not authorized to:",
          subpoints: [
            {
              letter: "a",
              text: `Represent yourself as an employee or agent with authority to bind ${brand}.`,
            },
            {
              letter: "b",
              text: `Sell products or services not approved by ${brand}.`,
            },
          ],
        },
      ],
    },
    {
      heading: "3. Your Responsibilities",
      body: [
        {
          number: "3.1",
          text: "You must:",
          subpoints: [
            {
              letter: "a",
              text: "Verify all customers’ identity and address details in line with RICA requirements.",
            },
            {
              letter: "b",
              text: "Safeguard any RICA devices or systems provided by MegsApp.",
            },
          ],
        },
        {
          number: "3.2",
          text: "You will operate at your own cost, including any expenses related to equipment, data, or operations.",
        },
        {
          number: "3.3",
          text: "Marketing Consent Disclosure:",
          subpoints: [
            {
              letter: "a",
              text: `You must explicitly inform every customer that by completing a RICA registration, they consent to receiving marketing communications from ${brand}.`,
            },
            {
              letter: "b",
              text: "You must specify that these communications may be sent via SMS, WhatsApp, email, push notifications, and other electronic channels.",
            },
          ],
        },
      ],
    },
    {
      heading: "4. MegsApp’s Responsibilities",
      body: [
        {
          number: "4.1",
          text: `${brand} will:`,
          subpoints: [
            {
              letter: "a",
              text: "Provide you with access to SIM cards and RICA systems.",
            },
            {
              letter: "b",
              text: "Offer training and support for RICA compliance.",
            },
          ],
        },
      ],
    },
    {
      heading: "5. Independent Contractor Relationship",
      body: [
        {
          number: "5.1",
          text: `You are not an employee of ${brand}. This agreement does not create an employment relationship.`,
        },
        {
          number: "5.2",
          text: `You have no authority to act or make decisions on behalf of ${brand}.`,
        },
      ],
    },
    {
      heading: "6. Confidentiality",
      body: [
        {
          number: "6.1",
          text: `You agree to maintain the confidentiality of all customer information and business details related to ${brand}.`,
        },
        {
          number: "6.2",
          text: "Customer information may only be used for RICA compliance purposes.",
        },
      ],
    },
    {
      heading: "7. Termination",
      body: [
        {
          number: "7.1",
          text: "This agreement may be terminated by either party with 14 days’ written notice.",
        },
        {
          number: "7.2",
          text: "MegsApp reserves the right to terminate this agreement immediately for non-compliance with RICA regulations or breach of these terms and conditions.",
        },
      ],
    },
    {
      heading: "8. Liability and Indemnity",
      body: [
        {
          number: "8.1",
          text: "You are responsible for ensuring compliance with RICA regulations.",
        },
        {
          number: "7.2",
          text: `You indemnify ${brand} against any losses, fines, or damages arising from your actions or non-compliance.`,
        },
      ],
    },
    {
      heading: "9. General Provisions",
      body: [
        {
          number: "9.1",
          text: "This agreement is governed by the laws of South Africa.",
        },
        {
          number: "9.2",
          text: "Any disputes will be resolved through negotiation or arbitration.",
        },
      ],
    },
    {
      heading: "10.	Acknowledgment and Acceptance",
      body: [
        {
          number: "",
          text: "By ticking the acceptance box during registration, you confirm that:",
          subpoints: [
            {
              letter: "a",
              text: "You have read and understood these terms and conditions.",
            },
            {
              letter: "b",
              text: "You agree to comply with RICA regulations and the requirements outlined here.",
            },
            {
              letter: "c",
              text: `You accept that this agreement constitutes a legally binding contract between you and ${brand}.`,
            },
          ],
        },
      ],
    },
  ];

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      const response = await acceptTermsHandle(fullName, username);
      if (response.status_code === 1001) {
        localStorage.setItem("terms_and_conditions_date", new Date().toISOString());
        handleClose();
      }
    } catch (error) {
      console.error("Error fetching account history", error);
    }
    setIsLoading(false);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.90)" },
      }}
    >
      <DialogTitle>ONLINE RICA AGENT TERMS AND CONDITIONS</DialogTitle>
      <DialogContent
        dividers
        sx={{
          maxHeight: "700px",
          overflowY: "auto",
          ml: 0.5,
        }}
      >
        <Typography variant="body1" paragraph>
          By registering as a RICA agent for{" "}
          <b>
            {" "}
            {brand}
          </b>{" "}
          and ticking the acceptance box, you agree to the following terms and conditions:
        </Typography>
        <Box>
          {modalContent.map((section, index) => (
            <Box key={index} sx={{ mt: index > 0 ? 2 : 0 }}>
              <Typography variant="h6">{section.heading}</Typography>
              {section.body.map((item, idx) => (
                <Box key={idx} sx={{ mt: idx > 0 ? 0.75 : 0 }}>
                  <Typography variant={item.subpoints ? "subtitle2" : "body2"} sx={{ ml: 2, display: "flex", gap: 1, mt: 1 }}>
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      {item.number}
                    </Box>
                    {item.text}
                  </Typography>
                  {item.subpoints &&
                    item.subpoints.map((sub, subIdx) => (
                      <Typography key={subIdx} variant="body2" sx={{ ml: 4, display: "flex", gap: 1, mt: 0.5 }}>
                        <Box component="span" sx={{ fontWeight: "bold" }}>
                          {sub.letter}.
                        </Box>
                        {sub.text}
                      </Typography>
                    ))}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FormControlLabel
            control={<Checkbox color="primary" checked={termsChecked} onChange={(e) => setTermsChecked(e.target.checked)} />}
            label={<Typography variant="body2">I have read and agree to the RICA Agent Terms and Conditions.</Typography>}
            sx={{ mt: 2 }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
          <>
            <TextField label="Enter Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} sx={{ width: "70%", mt: 2 }} />
            <LoadingButton
              loading={isLoading}
              variant="contained"
              color="primary"
              sx={{ width: "33%", mt: 4 }}
              onClick={handleAccept}
              disabled={!fullName || !termsChecked}
            >
              Accept
            </LoadingButton>
          </>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default TermsAndConditionsModal;
